import * as React from 'react';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/shadcn/components/ui/sheet';
import { Button } from '@/shadcn/components/ui/button';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { Label } from '@/shadcn/components/ui/label';
import { Input } from '@/shadcn/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/shadcn/components/ui/select';

// Define types for better type safety
type BetweenValue = { min: string; max: string };
type NumericFilterValue = string | BetweenValue;

interface RefreshMemberTableFilterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPayoutIds: number[];
  setSelectedPayoutIds: (ids: number[]) => void;
  selectedLastPayoutIds?: number[];
  setSelectedLastPayoutIds?: (ids: number[]) => void;
  numericFilters: Record<string, string[]>;
  setNumericFilters: (filters: Record<string, string[]>) => void;
  selectedStatusFilters: string[];
  setSelectedStatusFilters: (filters: string[]) => void;
}

export function RefreshMemberTableFilter({
  open,
  onOpenChange,
  selectedPayoutIds,
  setSelectedPayoutIds,
  selectedLastPayoutIds = [],
  setSelectedLastPayoutIds = () => { },
  numericFilters,
  setNumericFilters,
  selectedStatusFilters,
  setSelectedStatusFilters,
}: RefreshMemberTableFilterProps) {
  const {
    offer,
  } = useOfferDetailsContext() || {};
  const [searchText, setSearchText] = React.useState('');
  const [lastPayoutSearchText, setLastPayoutSearchText] = React.useState('');
  const { automatePayoutTier } = useClientFeatures();
  const isAutomatedPayoutEnabled = offer?.offerAutomationConfig?.isActive || false;

  // Use -1 to represent null payout IDs (Automated Tiers)
  const AUTOMATED_TIERS_ID = -1;

  const numericColumns = [
    { id: 'clicks', label: 'Clicks' },
    { id: 'conversions', label: 'Conversions' },
    { id: 'sales', label: 'Sales Amount' },
    { id: 'payoutEarned', label: 'Commissions Earned' },
    { id: 'payoutMade', label: 'Payouts' },
    { id: 'avgSale', label: 'Avg Sale' },
  ];
  const statusOptions = [
    { id: 'Active', label: 'Active' },
    { id: 'Deactivated', label: 'Deactivated' },
    { id: 'Failed', label: 'Failed' },
    { id: 'Expired', label: 'Expired' },
    { id: 'Upcoming', label: 'Upcoming' },
    { id: 'Others', label: 'Others' },
  ];

  // Helper function to parse existing comma-separated values
  function parseExistingFilter(filter: string[]): { operator: string; value: NumericFilterValue } {
    if (!filter || filter.length < 2) {
      return { operator: '', value: '' };
    }

    const [operator, value] = filter;

    if (operator === 'between' && typeof value === 'string' && value.includes(',')) {
      const [min, max] = value.split(',');
      return { operator, value: { min: min || '', max: max || '' } };
    }

    return { operator, value };
  }

  // Helper function to convert back to string format for API compatibility
  function convertFilterToString(operator: string, value: NumericFilterValue): string[] {
    if (operator === 'between' && typeof value === 'object') {
      return [operator, `${value.min},${value.max}`];
    }
    return [operator, value as string];
  }

  // Initialize local filters with proper parsing
  const [localNumericFilters, setLocalNumericFilters] = React.useState<Record<string, { operator: string; value: NumericFilterValue }>>(() => {
    const parsed: Record<string, { operator: string; value: NumericFilterValue }> = {};
    Object.entries(numericFilters).forEach(([key, filter]) => {
      parsed[key] = parseExistingFilter(filter);
    });
    return parsed;
  });
  const [localSelectedStatusFilters, setLocalSelectedStatusFilters] = React.useState<string[]>(selectedStatusFilters);

  React.useEffect(() => {
    const parsed: Record<string, { operator: string; value: NumericFilterValue }> = {};
    Object.entries(numericFilters).forEach(([key, filter]) => {
      parsed[key] = parseExistingFilter(filter);
    });
    setLocalNumericFilters(parsed);
    setLocalSelectedStatusFilters(selectedStatusFilters);
  }, [numericFilters, selectedStatusFilters]);

  function handleNumericFilterChange(columnId: string, operator: string, value: NumericFilterValue) {
    setLocalNumericFilters((prev) => ({ ...prev, [columnId]: { operator, value } }));
  }

  function handleClearNumericFilter(columnId: string) {
    setLocalNumericFilters((prev) => {
      const updated = { ...prev };
      delete updated[columnId];
      return updated;
    });
  }

  function handleBetweenValueChange(columnId: string, field: 'min' | 'max', newValue: string) {
    setLocalNumericFilters((prev) => {
      const current = prev[columnId];
      if (!current || current.operator !== 'between') return prev;

      const currentValue = typeof current.value === 'object' ? current.value : { min: '', max: '' };
      const updatedValue = { ...currentValue, [field]: newValue };

      // Validate min/max values
      if (field === 'min' && updatedValue.max && parseFloat(newValue) > parseFloat(updatedValue.max)) {
        // Swap values if min > max
        updatedValue.min = updatedValue.max;
        updatedValue.max = newValue;
      } else if (field === 'max' && updatedValue.min && parseFloat(newValue) < parseFloat(updatedValue.min)) {
        // Swap values if max < min
        updatedValue.max = updatedValue.min;
        updatedValue.min = newValue;
      }

      return { ...prev, [columnId]: { ...current, value: updatedValue } };
    });
  }

  function handleStatusFilterChange(status: string, checked: boolean) {
    setLocalSelectedStatusFilters((prev) =>
      (checked ? [...prev, status] : prev.filter((s) => s !== status))
    );
  }

  function handleApply() {
    // Convert local filters back to string format for API compatibility
    const convertedFilters: Record<string, string[]> = {};
    Object.entries(localNumericFilters).forEach(([key, filter]) => {
      convertedFilters[key] = convertFilterToString(filter.operator, filter.value);
    });

    setNumericFilters(convertedFilters);
    setSelectedStatusFilters(localSelectedStatusFilters);
    onOpenChange(false);
  }

  function handleCancel() {
    const parsed: Record<string, { operator: string; value: NumericFilterValue }> = {};
    Object.entries(numericFilters).forEach(([key, filter]) => {
      parsed[key] = parseExistingFilter(filter);
    });
    setLocalNumericFilters(parsed);
    setLocalSelectedStatusFilters(selectedStatusFilters);
    onOpenChange(false);
  }

  // Validation function to check if any numeric filter has operator but no value
  function hasInvalidNumericFilters(): boolean {
    return Object.values(localNumericFilters).some((filter) => {
      if (!filter.operator) return false; // No operator selected, skip validation

      if (filter.operator === 'between') {
        const value = filter.value as BetweenValue;
        return !value.min && !value.max; // Both min and max are empty
      } else {
        return !filter.value || filter.value === ''; // Value is empty
      }
    });
  }

  function handleToggle(id: number) {
    setSelectedPayoutIds(
      selectedPayoutIds.includes(id)
        ? selectedPayoutIds.filter((pid) => pid !== id)
        : [...selectedPayoutIds, id]
    );
  }

  function handleLastPayoutToggle(id: number) {
    setSelectedLastPayoutIds(
      selectedLastPayoutIds.includes(id)
        ? selectedLastPayoutIds.filter((pid) => pid !== id)
        : [...selectedLastPayoutIds, id]
    );
  }

  function handleDeselectAll() {
    setSelectedPayoutIds([]);
  }

  function handleLastPayoutDeselectAll() {
    setSelectedLastPayoutIds([]);
  }

  const filteredPayouts = React.useMemo(() => {
    if (!searchText) {
      if (automatePayoutTier) {
        const automatedPayouts = offer?.payouts?.filter((payout) =>
          typeof payout.offerAutomationConfigId === 'number' && payout.offerAutomationConfigId >= 0
        ) || [];
        const regularPayouts = offer?.payouts?.filter((payout) =>
          payout.offerAutomationConfigId === undefined || payout.offerAutomationConfigId === null
        ) || [];

        const allPayouts = [];

        // Add Automated Tiers option if there are automated payouts
        if (automatedPayouts.length > 0) {
          allPayouts.push({ id: AUTOMATED_TIERS_ID, label: 'Automated Tiers', isAutomated: true });
        }

        // Add regular payouts
        allPayouts.push(...regularPayouts);

        return allPayouts;
      }
      return offer?.payouts || [];
    }

    // Filter by search text
    const payoutsToFilter = automatePayoutTier
      ? offer?.payouts?.filter((payout) =>
        payout.offerAutomationConfigId === undefined || payout.offerAutomationConfigId === null
      ) || []
      : offer?.payouts || [];

    return payoutsToFilter.filter((payout) =>
      payout.label.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [offer?.payouts, searchText, automatePayoutTier, AUTOMATED_TIERS_ID]);

  const filteredLastPayouts = React.useMemo(() => {
    if (!lastPayoutSearchText) {
      // Show all payouts in Last Payout section
      return offer?.payouts || [];
    }

    return (offer?.payouts || []).filter((payout) =>
      payout.label.toLowerCase().includes(lastPayoutSearchText.toLowerCase())
    );
  }, [offer?.payouts, lastPayoutSearchText]);

  const getPayoutLabel = (payout) => {
    if ('isAutomated' in payout && payout.isAutomated) {
      return payout.label;
    }

    const rules = payout.offerPayoutRules || payout.payoutRules || [];
    const hasFlat = typeof payout.flatPayout === 'number';
    const hasPercent = typeof payout.percentPayout === 'number';

    if (rules.length > 1) {
      return `${payout.label} (Multiple)`;
    } else if (hasFlat && hasPercent) {
      return `${payout.label} ($${payout.flatPayout} + ${payout.percentPayout}%)`;
    } else if (hasFlat) {
      return `${payout.label} ($${payout.flatPayout})`;
    } else if (hasPercent) {
      return `${payout.label} (${payout.percentPayout}%)`;
    }
    return payout.label;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex flex-col h-full p-0 gap-0">
        <SheetHeader className="p-4">
          <SheetTitle className="m-0">Filters</SheetTitle>
        </SheetHeader>
        <div className="border-b" />
        <div className="flex-1 p-4 flex flex-col gap-4 overflow-y-auto">
          <Accordion type="multiple" defaultValue={['payouts', 'last-payout', 'status']} className="w-full">
            <AccordionItem value="payouts" className="border-none">
              <AccordionTrigger className="py-2 hover:no-underline">
                <Label className="text-sm font-medium">Payouts</Label>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2">
                  <div className="mb-2 relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                    <Input
                      type="search"
                      placeholder="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      className="rounded-lg bg-background pl-8 w-full focus:ring-0 focus-visible:ring-0 outline-none"
                    />
                  </div>
                  <div>
                    <div className="mb-2 text-muted-foreground cursor-pointer underline text-sm" onClick={handleDeselectAll}>Deselect All</div>
                    {filteredPayouts.map((payout) => (
                      <div key={payout.id} className="flex items-center gap-2 mb-2">
                        <Checkbox
                          checked={selectedPayoutIds.includes(payout.id)}
                          onCheckedChange={() => handleToggle(payout.id)}
                          id={`payout-${payout.id}`}
                        />
                        <Label className="text-sm font-normal text-primary" htmlFor={`payout-${payout.id}`}>{getPayoutLabel(payout)}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            <div className="border-b" />
            {(automatePayoutTier && isAutomatedPayoutEnabled) && (
              <AccordionItem value="last-payout" className="border-none">
                <AccordionTrigger className="py-2 hover:no-underline">
                  <Label className="text-sm font-medium">Last Payout</Label>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-2 pt-2">
                    <div className="mb-2 relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                      <Input
                        type="search"
                        placeholder="Search"
                        value={lastPayoutSearchText}
                        onChange={(e) => setLastPayoutSearchText(e.target.value)}
                        className="rounded-lg bg-background pl-8 w-full focus:ring-0 focus-visible:ring-0 outline-none"
                      />
                    </div>
                    <div>
                      <div className="mb-2 text-muted-foreground cursor-pointer underline text-sm" onClick={handleLastPayoutDeselectAll}>Deselect All</div>
                      {filteredLastPayouts.map((payout) => (
                        <div key={`last-${payout.id}`} className="flex items-center gap-2 mb-2">
                          <Checkbox
                            checked={selectedLastPayoutIds.includes(payout.id)}
                            onCheckedChange={() => handleLastPayoutToggle(payout.id)}
                            id={`last-payout-${payout.id}`}
                          />
                          <Label className="text-sm font-normal text-primary" htmlFor={`last-payout-${payout.id}`}>{getPayoutLabel(payout)}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
            <div className="border-b" />

            <AccordionItem value="status" className="border-none">
              <AccordionTrigger className="py-2 hover:no-underline">
                <Label className="text-sm font-medium">Status</Label>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2">
                  {statusOptions.map((option) => (
                    <div key={option.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={localSelectedStatusFilters.includes(option.id)}
                        onCheckedChange={(checked) => handleStatusFilterChange(option.id, checked as boolean)}
                        id={`status-${option.id}`}
                      />
                      <Label className="text-sm font-normal text-primary" htmlFor={`status-${option.id}`}>{option.label}</Label>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
            <div className="border-b" />

            <AccordionItem value="kpi-filters" className="border-none">
              <AccordionTrigger className="py-2 hover:no-underline">
                <Label className="text-sm font-medium">KPI Filters</Label>
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-4 pt-2">
                  <Label className="text-sm font-medium">Engagement</Label>
                  {numericColumns.slice(0, 2).map((column) => {
                    const currentFilter = localNumericFilters[column.id] || { operator: '', value: '' };
                    const { operator, value } = currentFilter;
                    return (
                      <div key={column.id} className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs font-medium">{column.label}</Label>
                          {operator && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleClearNumericFilter(column.id)}
                              className="text-xs text-muted-foreground hover:text-destructive h-6 px-2"
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Select
                            value={operator}
                            onValueChange={(newOperator) => handleNumericFilterChange(column.id, newOperator, value)}
                          >
                            <SelectTrigger className="w-32 h-8 text-sm">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gt">Greater than</SelectItem>
                              <SelectItem value="lt">Less than</SelectItem>
                              <SelectItem value="between">Between</SelectItem>
                              <SelectItem value="eq">Equal to</SelectItem>
                            </SelectContent>
                          </Select>
                          {operator === 'between' ? (
                            <>
                              <Input
                                type="number"
                                placeholder="Min"
                                className="w-20 h-8 text-xs"
                                value={typeof value === 'object' ? value.min || '' : ''}
                                onChange={(e) => handleBetweenValueChange(column.id, 'min', e.target.value)}
                                min={0}
                              />
                              <span className="mx-1">-</span>
                              <Input
                                type="number"
                                placeholder="Max"
                                className="w-20 h-8 text-xs"
                                value={typeof value === 'object' ? value.max || '' : ''}
                                onChange={(e) => handleBetweenValueChange(column.id, 'max', e.target.value)}
                                min={0}
                              />
                            </>
                          ) : (
                            <Input
                              type="number"
                              placeholder="Value"
                              className="flex-1 h-8 text-xs"
                              value={typeof value === 'string' ? value : ''}
                              onChange={(e) => handleNumericFilterChange(column.id, operator, e.target.value)}
                              min={0}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className="border-b" />
                  <Label className="text-sm font-medium">Financial</Label>
                  {numericColumns.slice(2).map((column) => {
                    const currentFilter = localNumericFilters[column.id] || { operator: '', value: '' };
                    const { operator, value } = currentFilter;
                    return (
                      <div key={column.id} className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs font-medium">{column.label}</Label>
                          {operator && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleClearNumericFilter(column.id)}
                              className="text-xs text-muted-foreground hover:text-destructive h-6 px-2"
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Select
                            value={operator}
                            onValueChange={(newOperator) => handleNumericFilterChange(column.id, newOperator, value)}
                          >
                            <SelectTrigger className="w-32 h-8 text-sm">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gt">Greater than</SelectItem>
                              <SelectItem value="lt">Less than</SelectItem>
                              <SelectItem value="between">Between</SelectItem>
                              <SelectItem value="eq">Equal to</SelectItem>
                            </SelectContent>
                          </Select>
                          {operator === 'between' ? (
                            <>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                                <Input
                                  type="number"
                                  placeholder="Min"
                                  className="w-20 h-8 text-xs pl-5"
                                  value={typeof value === 'object' ? value.min || '' : ''}
                                  onChange={(e) => handleBetweenValueChange(column.id, 'min', e.target.value)}
                                  min={0}
                                />
                              </div>
                              <span className="mx-1">-</span>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                                <Input
                                  type="number"
                                  placeholder="Max"
                                  className="w-20 h-8 text-xs pl-5"
                                  value={typeof value === 'object' ? value.max || '' : ''}
                                  onChange={(e) => handleBetweenValueChange(column.id, 'max', e.target.value)}
                                  min={0}
                                />
                              </div>
                            </>
                          ) : (
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                              <Input
                                type="number"
                                placeholder="Value"
                                className="flex-1 h-8 text-xs pl-5"
                                value={typeof value === 'string' ? value : ''}
                                onChange={(e) => handleNumericFilterChange(column.id, operator, e.target.value)}
                                min={0}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        <div className="border-t" />
        <SheetFooter className="justify-start sm:justify-start p-4">
          <Button onClick={handleApply} disabled={hasInvalidNumericFilters()}>Apply</Button>
          <Button variant="outline" onClick={handleCancel}>Cancel</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
